import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import './properties.css';

const defaultForm = {
    name: '',
    address1: '',
    city: '',
    state: '',
    neighborhood: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    bedrooms: '',
    bathrooms: '',
    acquisitionDate: '',
    rentStartDate: '',
    rentCost: '',
    wifiCost: '40.21',
    cleaningCost: '',
    suppliesCost: '',
    otherCost: '',
    pricingToolsCost: '21'
};

const STEPS = [
    { label: 'Location' },
    { label: 'About' },
    { label: 'Costs' },
    { label: 'Investments' },
];

const PropertyCreatePage = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState(defaultForm);
    const [investments, setInvestments] = useState({
        deposit: '',
        furniture: '',
        moversAndTransportation: '',
        applicationFees: '',
        photographer: '',
        other: '',
    });
    const [step, setStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const onChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const onInvestmentChange = (event) => {
        const { name, value } = event.target;
        setInvestments((prev) => ({ ...prev, [name]: value }));
    };

    const onNext = (event) => {
        event.preventDefault();
        setStep((s) => s + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const onBack = () => {
        setStep((s) => s - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            const payload = {
                ...form,
                acquisitionDate: form.acquisitionDate || null,
                rentStartDate: form.rentStartDate || null,
                rentCost: parseFloat(form.rentCost) || 0,
                wifiCost: parseFloat(form.wifiCost) || 0,
                cleaningCost: parseFloat(form.cleaningCost) || 0,
                suppliesCost: parseFloat(form.suppliesCost) || 0,
                otherCost: parseFloat(form.otherCost) || 0,
                pricingToolsCost: parseFloat(form.pricingToolsCost) || 0,
                latitude: form.latitude === '' ? null : parseFloat(form.latitude),
                longitude: form.longitude === '' ? null : parseFloat(form.longitude)
            };

            const response = await api.post('/property', payload);
            const propertyId = Number(response?.data?.id);
            const geocodeWarning = response?.data?.geocodeWarning || '';

            if (Number.isInteger(propertyId) && propertyId > 0) {
                const INVESTMENT_LABELS = {
                    deposit: 'Deposit',
                    furniture: 'Furniture',
                    moversAndTransportation: 'Movers and Transportation',
                    applicationFees: 'Application Fees',
                    photographer: 'Photographer',
                    other: 'Other',
                };
                const investmentEntries = Object.entries(investments)
                    .map(([key, val]) => ({ reason: INVESTMENT_LABELS[key], cost: parseFloat(val) }))
                    .filter(({ cost }) => Number.isFinite(cost) && cost > 0);

                await Promise.all(
                    investmentEntries.map(({ reason, cost }) =>
                        api.post(`/investment/property/${propertyId}`, { reason, cost })
                    )
                );

                navigate(`/PropertyDetails/${propertyId}`, geocodeWarning ? { state: { geocodeWarning } } : undefined);
                return;
            }

            navigate('/Properties');
        } catch (e) {
            setMessage({
                type: 'error',
                text: e?.response?.data?.error || 'Unable to create property right now.'
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="properties-container property-create-page">
            <div className="property-create-header">
                <div className="property-create-header-top">
                    <button className="btn-link" type="button" onClick={() => navigate('/Properties')}>
                        ← Back
                    </button>
                    <h2>Create New Property</h2>
                </div>
                <div className="property-create-stepper">
                    {STEPS.map((s, i) => (
                        <div key={s.label} className={`property-create-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
                            <div className="property-create-step-dot">{i < step ? '✓' : i + 1}</div>
                            <span>{s.label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {message.text && (
                <div className={`direct-booking-alert ${message.type === 'success' ? 'direct-booking-alert-success' : 'direct-booking-alert-error'}`}>
                    {message.text}
                </div>
            )}

            {step === 0 && (
                <form className="property-create-form" onSubmit={onNext}>
                    <section className="property-create-card">
                        <div className="property-create-card-header">
                            <div>
                                <p className="property-create-kicker">Step 1 of 4</p>
                                <h2>Location</h2>
                            </div>
                            <span className="property-create-card-caption">Where is this property located?</span>
                        </div>
                        <div className="property-create-grid">
                            <label>
                                Address
                                <input name="address1" placeholder="Street address" value={form.address1} onChange={onChange} required />
                            </label>
                            <label>
                                City
                                <input name="city" placeholder="City" value={form.city} onChange={onChange} required />
                            </label>
                            <label>
                                State
                                <input name="state" placeholder="State" value={form.state} onChange={onChange} required />
                            </label>
                            <label>
                                Postal Code
                                <input name="postalCode" placeholder="Postal Code" value={form.postalCode} onChange={onChange} />
                            </label>
                            <label>
                                Neighborhood
                                <input name="neighborhood" placeholder="Neighborhood (optional)" value={form.neighborhood} onChange={onChange} />
                            </label>
                            <label>
                                Latitude
                                <input name="latitude" type="number" step="any" placeholder="e.g. 33.749000 (optional, for map)" value={form.latitude} onChange={onChange} />
                            </label>
                            <label>
                                Longitude
                                <input name="longitude" type="number" step="any" placeholder="e.g. -84.388000 (optional, for map)" value={form.longitude} onChange={onChange} />
                            </label>
                        </div>
                    </section>
                    <div className="property-create-actions">
                        <button className="btn-secondary" type="button" onClick={() => navigate('/Properties')}>
                            Cancel
                        </button>
                        <button className="btn-primary" type="submit">
                            Next: About the Property →
                        </button>
                    </div>
                </form>
            )}

            {step === 1 && (
                <form className="property-create-form" onSubmit={onNext}>
                    <section className="property-create-card">
                        <div className="property-create-card-header">
                            <div>
                                <p className="property-create-kicker">Step 2 of 4</p>
                                <h2>About the Property</h2>
                            </div>
                            <span className="property-create-card-caption">Name and size details used across your portfolio views.</span>
                        </div>
                        <div className="property-create-grid">
                            <label>
                                Property Name
                                <input name="name" placeholder="e.g. Beachside Bungalow" value={form.name} onChange={onChange} required />
                            </label>
                            <label>
                                Bedrooms
                                <input name="bedrooms" type="number" placeholder="Number of bedrooms" value={form.bedrooms} onChange={onChange} />
                            </label>
                            <label>
                                Bathrooms
                                <input name="bathrooms" type="number" placeholder="Number of bathrooms" value={form.bathrooms} onChange={onChange} />
                            </label>
                        </div>
                    </section>
                    <div className="property-create-actions">
                        <button className="btn-secondary" type="button" onClick={onBack}>
                            ← Back
                        </button>
                        <button className="btn-primary" type="submit">
                            Next: Costs and Dates →
                        </button>
                    </div>
                </form>
            )}

            {step === 2 && (
                <form className="property-create-form" onSubmit={onNext}>
                    <section className="property-create-card">
                        <div className="property-create-card-header">
                            <div>
                                <p className="property-create-kicker">Step 3 of 4</p>
                                <h2>Costs and Start Dates</h2>
                            </div>
                            <span className="property-create-card-caption">These defaults feed your reporting, property health, and booking math.</span>
                        </div>
                        <div className="property-create-grid">
                            <label>
                                Acquisition Date
                                <input name="acquisitionDate" type="date" value={form.acquisitionDate} onChange={onChange} />
                            </label>
                            <label>
                                Rent Start Date
                                <input name="rentStartDate" type="date" value={form.rentStartDate} onChange={onChange} />
                            </label>
                            <label>
                                Monthly Rent Amount
                                <input name="rentCost" type="number" step="0.01" placeholder="0.00" value={form.rentCost} onChange={onChange} />
                            </label>
                            <label>
                                Monthly WiFi Cost
                                <input name="wifiCost" type="number" step="0.01" placeholder="0.00" value={form.wifiCost} onChange={onChange} />
                            </label>
                            <label>
                                Cleaning Cost per Booking
                                <input name="cleaningCost" type="number" step="0.01" placeholder="0.00" value={form.cleaningCost} onChange={onChange} />
                            </label>
                            <label>
                                Default Monthly Supplies Cost
                                <input name="suppliesCost" type="number" step="0.01" placeholder="0.00" value={form.suppliesCost} onChange={onChange} />
                            </label>
                            <label>
                                Other Monthly Cost
                                <input name="otherCost" type="number" step="0.01" placeholder="0.00" value={form.otherCost} onChange={onChange} />
                            </label>
                            <label>
                                Pricing Tools Cost
                                <input name="pricingToolsCost" type="number" step="0.01" placeholder="0.00" value={form.pricingToolsCost} onChange={onChange} />
                            </label>
                        </div>
                    </section>
                    <div className="property-create-actions">
                        <button className="btn-secondary" type="button" onClick={onBack}>
                            ← Back
                        </button>
                        <button className="btn-primary" type="submit">
                            Next: Investments →
                        </button>
                    </div>
                </form>
            )}

            {step === 3 && (
                <form className="property-create-form" onSubmit={onSubmit}>
                    <section className="property-create-card">
                        <div className="property-create-card-header">
                            <div>
                                <p className="property-create-kicker">Step 4 of 4</p>
                                <h2>Initial Investments</h2>
                            </div>
                            <span className="property-create-card-caption">One-time startup costs. Leave blank or enter 0 to skip.</span>
                        </div>
                        <div className="property-create-grid">
                            <label>
                                Deposit
                                <input name="deposit" type="number" step="0.01" placeholder="0.00" value={investments.deposit} onChange={onInvestmentChange} />
                            </label>
                            <label>
                                Furniture
                                <input name="furniture" type="number" step="0.01" placeholder="0.00" value={investments.furniture} onChange={onInvestmentChange} />
                            </label>
                            <label>
                                Movers and Transportation
                                <input name="moversAndTransportation" type="number" step="0.01" placeholder="0.00" value={investments.moversAndTransportation} onChange={onInvestmentChange} />
                            </label>
                            <label>
                                Application Fees
                                <input name="applicationFees" type="number" step="0.01" placeholder="0.00" value={investments.applicationFees} onChange={onInvestmentChange} />
                            </label>
                            <label>
                                Photographer
                                <input name="photographer" type="number" step="0.01" placeholder="0.00" value={investments.photographer} onChange={onInvestmentChange} />
                            </label>
                            <label>
                                Other
                                <input name="other" type="number" step="0.01" placeholder="0.00" value={investments.other} onChange={onInvestmentChange} />
                            </label>
                        </div>
                    </section>
                    <div className="property-create-actions">
                        <button className="btn-secondary" type="button" onClick={onBack}>
                            ← Back
                        </button>
                        <button className="btn-primary" type="submit" disabled={saving}>
                            {saving ? 'Creating Property...' : 'Create Property'}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default PropertyCreatePage;
